import { BaseCheckboxInput, Button } from '@capsicum/common-web';
import {
	NCOnboardingAnalyticsEvents,
	type NCOnboardingPageProps,
	useNCWOnboardingRoutes
} from '@capsicum/features/nc-onboarding/shared';
import { useUserAgreement, useUserAgreementActions, useUserAgreementData } from '@capsicum/socios-core';
import { Shell } from '@capsicum/socios-core-web';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useNCWExistingUsersTranslations } from '../../hooks';

type CreateYourWalletScreenProps = Pick<NCOnboardingPageProps, 'onDone' | 'onTellMeMore'>;

export const CreateYourWalletScreen = ({ onDone, onTellMeMore }: CreateYourWalletScreenProps) => {
	const { userActiveAgreement, userActiveAgreementIsLoading } = useUserAgreement('user_agreement_ncw');
	const { link } = useUserAgreementData(userActiveAgreement);
	const { acceptUserAgreement, userAcceptAgreementIsLoading } = useUserAgreementActions(userActiveAgreement);
	const $t = useNCWExistingUsersTranslations(link);
	const { triggerAnalyticsEvent } = useNCWOnboardingRoutes({});
	const [areTermsAccepted, setTermsAccepted] = useState<boolean>(false);

	const onClick = async () => {
		await acceptUserAgreement();
		onDone();
	};

	const isButtonLoading = userActiveAgreementIsLoading || userAcceptAgreementIsLoading;

	useEffect(() => {
		triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.LAND_ON_CREATE_WALLET_STEPS_SCREEN);
	}, [triggerAnalyticsEvent]);

	return (
		<S.Wrapper>
			<Shell
				buttons={[
					<Button
						disabled={!areTermsAccepted}
						fullWidth
						isLoading={isButtonLoading}
						key="confirm"
						testID="create-wallet-continue-button"
						text={$t('ncOnboard.createYourWallet.actions.button')}
						onClick={onClick}
					/>,
					<S.TellMeMoreButton key="tellmeMore" onClick={onTellMeMore}>
						{$t('ncOnboard.createYourWallet.actions.tellmeMore')}
					</S.TellMeMoreButton>
				]}
				buttonsPosition="column"
			>
				<S.Content>
					<S.Header>
						<S.Title>{$t('ncOnboard.createYourWallet.title')}</S.Title>
						<S.SubHeading>{$t('ncOnboard.createYourWallet.subtitle')}</S.SubHeading>
					</S.Header>
					<S.Content>
						<S.Section>
							<S.SectionTitle>{$t('ncOnboard.createYourWallet.notes.one.title')}</S.SectionTitle>
							<S.SectionDescription>
								{$t('ncOnboard.createYourWallet.notes.one.description')}
							</S.SectionDescription>
						</S.Section>
						<S.Section>
							<S.SectionTitle>{$t('ncOnboard.createYourWallet.notes.two.title')}</S.SectionTitle>
							<S.SectionDescription>
								{$t('ncOnboard.createYourWallet.notes.two.description')}
							</S.SectionDescription>
						</S.Section>
						<S.Section>
							<S.SectionTitle>{$t('ncOnboard.createYourWallet.notes.three.title')}</S.SectionTitle>
							<S.SectionDescription>
								<S.List>
									<S.ListItem>{`\u2022 ${$t('ncOnboard.createYourWallet.notes.three.benefits.one')}`}</S.ListItem>
									<S.ListItem>{`\u2022 ${$t('ncOnboard.createYourWallet.notes.three.benefits.two')}`}</S.ListItem>
									<S.ListItem>{`\u2022 ${$t('ncOnboard.createYourWallet.notes.three.benefits.three')}`}</S.ListItem>
								</S.List>
							</S.SectionDescription>
						</S.Section>
					</S.Content>
					<BaseCheckboxInput testID="onboard-terms&conditions-checkbox" onChange={setTermsAccepted}>
						<S.ListItem>{$t('ncOnboard.createYourWallet.terms')}</S.ListItem>
					</BaseCheckboxInput>
				</S.Content>
			</Shell>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		flex: 1;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		height: 100%;
	`;

	export const Header = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const Title = styled.div`
		text-transform: uppercase;
		${({ theme }) => theme.fonts.headings.display3};
	`;

	export const SubHeading = styled.div`
		${({ theme }) => theme.fonts.text.text9};
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const Section = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const SectionTitle = styled.div`
		${({ theme }) => theme.fonts.text.textTopBar};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const SectionDescription = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		margin-left: ${({ theme }) => theme.spacings.small};
	`;

	export const TellMeMoreButton = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		cursor: pointer;
	`;

	export const List = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const ListItem = styled.div`
		${({ theme }) => theme.fonts.text.text3};
	`;
}
